import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { isDesktop, loadResourceLibrary, saveResourceLibrary } from '../services/resourceStorage.js'

const STORAGE_KEY = 'hasp-dataset-library'

export const MAX_SERIES = 8

// Fixed order, one per series slot — never cycled/regenerated so a series'
// color stays tied to its position, and adjacent hues stay distinguishable.
export const SERIES_COLORS = [
  '#3987e5', // blue
  '#c98500', // yellow
  '#d95926', // orange
  '#008300', // green
  '#199e70', // aqua
  '#9085e9', // violet
  '#e66767', // red
  '#d55181', // magenta
]

// Single source of truth for the Dataset "type" field — used by the type
// dropdown and the unit auto-fill. `other` keeps its stored value for
// backward compatibility even though it now reads "Custom" in the UI.
export const DATASET_TYPES = [
  { value: 'temperature', label: 'Temperature', unit: '°C' },
  { value: 'humidity',    label: 'Humidity',    unit: '%' },
  { value: 'pressure',    label: 'Pressure',    unit: 'hPa' },
  { value: 'power',       label: 'Power',       unit: 'kWh' },
  { value: 'energy',      label: 'Energy',      unit: 'kWh' },
  { value: 'voltage',     label: 'Voltage',     unit: 'V' },
  { value: 'current',     label: 'Current',     unit: 'A' },
  { value: 'battery',     label: 'Battery',     unit: '%' },
  { value: 'illuminance', label: 'Illuminance', unit: 'lx' },
  { value: 'percentage',  label: 'Percentage',  unit: '%' },
  { value: 'other',       label: 'Custom',      unit: '' },
]

// ── Runtime statistics ────────────────────────────────────────────────────────
// Dataset-wide stats drive automatic Y-axis calculation; per-series stats are
// the upcoming Legend enhancements' data source. Both are runtime metadata
// populated by the firmware/live data, never authored — see toSnapshots().

function _newDatasetStats() {
  return { min: null, max: null, valid: false }
}

function _newSeriesStats() {
  return { current: null, min: null, max: null, avg: null, valid: false }
}

// ── Migration helpers ─────────────────────────────────────────────────────────
// Used only to upgrade datasets that stored history/interval on individual series
// (legacy format) to the new dataset-level fields.

function _parseLegacyHours(str) {
  if (str == null) return null
  const s = String(str).trim()
  if (/^\d+(?:\.\d+)?$/.test(s)) return parseFloat(s)      // bare number = hours
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(hour|hours|day|days|week|weeks|minute|minutes)/i)
  if (!m) return null
  const n = parseFloat(m[1])
  const u = m[2].toLowerCase()
  if (u.startsWith('hour'))   return n
  if (u.startsWith('day'))    return n * 24
  if (u.startsWith('week'))   return n * 168
  if (u.startsWith('minute')) return n / 60
  return null
}

function _parseLegacySeconds(str) {
  if (str == null) return null
  const s = String(str).trim()
  if (/^\d+(?:\.\d+)?$/.test(s)) return parseFloat(s) * 60  // bare number = minutes → seconds
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(minute|minutes|hour|hours)/i)
  if (!m) return null
  const n = parseFloat(m[1])
  const u = m[2].toLowerCase()
  if (u.startsWith('minute')) return n * 60
  if (u.startsWith('hour'))   return n * 3600
  return null
}

/**
 * Migrate a dataset from the legacy per-series history/interval format to
 * dataset-level fields.  Mutates ds in-place and returns any warning messages.
 */
function _migrateDataset(ds) {
  if (!ds.series?.length) return []

  const hasHistory  = ds.series.some(s => s.history  != null)
  const hasInterval = ds.series.some(s => s.interval != null)
  if (!hasHistory && !hasInterval) return []

  const warnings = []

  if (hasHistory && ds.history_hours == null) {
    const vals = [...new Set(
      ds.series.map(s => _parseLegacyHours(s.history)).filter(v => v != null),
    )]
    if (vals.length > 1) {
      warnings.push(
        `Dataset "${ds.name}": series had different history periods — normalized to ${vals[0]}h ` +
        `(first series value). Use separate Datasets if different periods are needed.`,
      )
    }
    ds.history_hours = vals[0] ?? 24
  }

  if (hasInterval && ds.interval == null) {
    const vals = [...new Set(
      ds.series.map(s => _parseLegacySeconds(s.interval)).filter(v => v != null),
    )]
    if (vals.length > 1) {
      warnings.push(
        `Dataset "${ds.name}": series had different sampling intervals — normalized to ${vals[0]}s ` +
        `(first series value). Use separate Datasets if different intervals are needed.`,
      )
    }
    ds.interval = vals[0] ?? 300
  }

  // Remove legacy per-series time fields
  for (const s of ds.series) {
    delete s.history
    delete s.interval
  }

  return warnings
}

/** Backfill stats objects on datasets/series persisted before they existed. */
function _ensureStats(ds) {
  if (!ds.stats) ds.stats = _newDatasetStats()
  for (const s of ds.series ?? []) {
    if (!s.stats) s.stats = _newSeriesStats()
  }
}

/** Bring stored datasets up to the current format; persists once when anything changed. */
function prepareDatasets(datasets) {
  const migrationWarnings = []
  for (const ds of datasets) {
    migrationWarnings.push(..._migrateDataset(ds))
    _ensureStats(ds)
  }
  if (migrationWarnings.length > 0) saveResourceLibrary('datasets', STORAGE_KEY, datasets)
  return { datasets, migrationWarnings }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return prepareDatasets(raw ? JSON.parse(raw) : [])
  } catch {
    return { datasets: [], migrationWarnings: [] }
  }
}

export const useDatasetStore = defineStore('datasets', {
  // The browser library loads synchronously; the desktop app's SQLite
  // library arrives through load() at startup.
  state: () => {
    const { datasets, migrationWarnings } = isDesktop() ? { datasets: [], migrationWarnings: [] } : loadFromStorage()
    return { datasets, migrationWarnings, loaded: !isDesktop() }
  },
  getters: {
    all: (state) => state.datasets,
  },
  actions: {
    /** Load the desktop app's SQLite library (no-op in the browser, which loads synchronously). */
    async load() {
      if (!isDesktop()) return
      const { datasets, migrationWarnings } = prepareDatasets(await loadResourceLibrary('datasets', STORAGE_KEY))
      this.datasets = datasets
      this.migrationWarnings = migrationWarnings
      this.loaded = true
    },
    _persist() {
      // Never let a save before load() replace the stored library with a partial one.
      if (!this.loaded) return
      try {
        saveResourceLibrary('datasets', STORAGE_KEY, this.datasets)
      } catch (e) {
        console.warn('Failed to persist dataset library:', e)
      }
    },
    // Lowest free id from the reserved floor of 44 upward — gap-filling
    // rather than always growing, so deleted ids get reused.
    _nextAvailableId() {
      const used = new Set(this.datasets.map(d => d.id))
      let id = 44
      while (used.has(id)) id++
      return id
    },
    clearMigrationWarnings() {
      this.migrationWarnings = []
    },

    // --- CRUD ---

    addDataset() {
      const dataset = {
        uuid: uuidv4(),
        id: this._nextAvailableId(),
        name: 'New Dataset',
        history_hours: 24,
        interval: 300,
        unit: '',
        type: 'other',
        stats: _newDatasetStats(),
        series: [],
      }
      this.datasets.push(dataset)
      this._persist()
      return dataset.uuid
    },
    removeDataset(uuid) {
      const idx = this.datasets.findIndex(d => d.uuid === uuid)
      if (idx >= 0) {
        this.datasets.splice(idx, 1)
        this._persist()
      }
    },
    duplicateDataset(uuid) {
      const src = this.datasets.find(d => d.uuid === uuid)
      if (!src) return null
      const copy = {
        ...JSON.parse(JSON.stringify(src)),
        uuid: uuidv4(),
        id: this._nextAvailableId(),
        name: src.name + ' (copy)',
        stats: _newDatasetStats(),
      }
      copy.series = copy.series.map(s => ({ ...s, stats: _newSeriesStats() }))
      this.datasets.push(copy)
      this._persist()
      return copy.uuid
    },
    getDataset(uuid) {
      return this.datasets.find(d => d.uuid === uuid) ?? null
    },
    updateDataset(uuid, patch) {
      const ds = this.datasets.find(d => d.uuid === uuid)
      if (!ds) return
      Object.assign(ds, patch)
      this._persist()
    },

    // --- Series ---

    addSeries(uuid) {
      const ds = this.datasets.find(d => d.uuid === uuid)
      if (!ds) return
      if (ds.series.length >= MAX_SERIES) return
      const color = SERIES_COLORS[ds.series.length % SERIES_COLORS.length]
      ds.series.push({ label: '', color, entity: '', stats: _newSeriesStats() })
      this._persist()
    },
    removeSeries(uuid, seriesIndex) {
      const ds = this.datasets.find(d => d.uuid === uuid)
      if (!ds) return
      ds.series.splice(seriesIndex, 1)
      this._persist()
    },
    moveSeries(uuid, fromIndex, toIndex) {
      const ds = this.datasets.find(d => d.uuid === uuid)
      if (!ds) return
      const [item] = ds.series.splice(fromIndex, 1)
      ds.series.splice(toIndex, 0, item)
      this._persist()
    },

    // --- Snapshot / portability ---

    importSnapshot(snapshot) {
      if (this.datasets.find(d => d.uuid === snapshot.uuid)) return snapshot.uuid
      const series = JSON.parse(JSON.stringify(snapshot.series ?? []))
        .map(s => ({ ...s, stats: _newSeriesStats() }))
      const dataset = {
        uuid:         snapshot.uuid,
        id:           snapshot.id ?? this._nextAvailableId(),
        name:         snapshot.name ?? 'Imported Dataset',
        history_hours: snapshot.history_hours ?? 24,
        interval:     snapshot.interval ?? 300,
        unit:         snapshot.unit ?? '',
        type:         snapshot.type ?? 'other',
        stats:        _newDatasetStats(),
        series,
      }
      const warnings = _migrateDataset(dataset)
      if (warnings.length > 0) this.migrationWarnings.push(...warnings)
      this.datasets.push(dataset)
      this._persist()
      return dataset.uuid
    },

    // Runtime stats are excluded from the snapshot — they're live metadata,
    // not part of the saved design's public format (see _newDatasetStats()).
    toSnapshots(uuids) {
      return [...uuids]
        .map(uuid => this.datasets.find(d => d.uuid === uuid))
        .filter(Boolean)
        .map(d => {
          const { stats, ...clone } = JSON.parse(JSON.stringify(d))
          clone.series = clone.series.map(({ stats, ...s }) => s)
          return clone
        })
    },

    findMissingFromSnapshots(snapshots) {
      return (snapshots ?? []).filter(s => !this.datasets.find(d => d.uuid === s.uuid))
    },

    // --- Import (plate/firmware resource line) ---

    /**
     * Reconstruct (or update) a Dataset from an imported
     * {"obj":"resource","kind":"dataset",...} line, matched by `id` (the
     * only identifier the wire format carries) — widgets that referenced
     * this dataset by uuid are not re-linked, since the wire format only
     * ever carried the numeric id, an accepted limitation of the current
     * design. Never carries history_hours/interval/name — those are
     * Designer-only fields with no firmware wire-format meaning.
     */
    importResource({ id, unit, type, series }) {
      const existing = this.datasets.find(d => d.id === id)
      if (existing) {
        if (unit !== undefined) existing.unit = unit
        if (type !== undefined) existing.type = type
        if (series !== undefined) {
          existing.series = JSON.parse(JSON.stringify(series)).map(s => ({ ...s, stats: _newSeriesStats() }))
        }
        this._persist()
        return existing.uuid
      }
      const dataset = {
        uuid: uuidv4(),
        id,
        name: `Dataset ${id}`,
        history_hours: 24,
        interval: 300,
        unit: unit ?? '',
        type: type ?? 'other',
        stats: _newDatasetStats(),
        series: JSON.parse(JSON.stringify(series ?? [])).map(s => ({ ...s, stats: _newSeriesStats() })),
      }
      this.datasets.push(dataset)
      this._persist()
      return dataset.uuid
    },
  },
})
